<script lang="ts">
import range from 'lodash/range';
import { computed, defineComponent, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import ButtonGroup from '@/components/base/ButtonGroup.vue';
import useButtonStyle, { buttonStyleProps } from '@/composables/useButtonStyles';
import money from '@/filters/money';

export default defineComponent({
  name: 'AddToCartButtonGroup',
  props: {
    ...buttonStyleProps,
    dataTest: { required: false, type: String },
    maximumPiecesPerOrder: { required: true, type: Number },
    quantity: { required: true, type: Number },
  },
  components: {
    ButtonGroup,
  },
  emits: ['update:quantity', 'submit'],
  setup(props, { emit }) {
    const route = useRoute();
    const buttonStyle = useButtonStyle(props);

    const altQuantityInput = ref<HTMLElement>();
    const showAltQuantity = ref(props.quantity >= 10);

    const selectedQuantity = computed({
      get: () => props.quantity,
      set: (val) => {
        if (val <= props.maximumPiecesPerOrder) {
          emit('update:quantity', val);
        } else {
          emit('update:quantity', props.maximumPiecesPerOrder);
        }
      },
    });

    const quantityOptions = computed(() => {
      const limit =
        props.maximumPiecesPerOrder && props.maximumPiecesPerOrder < 10
          ? props.maximumPiecesPerOrder
          : 9;
      return range(1, limit + 1);
    });

    const showMoreQuantityOption = computed(() =>
      props.maximumPiecesPerOrder ? props.maximumPiecesPerOrder >= 10 : true,
    );

    watch(selectedQuantity, (val) => {
      if (val >= 10) {
        showAltQuantity.value = true;
        setTimeout(() => {
          altQuantityInput.value?.focus();
        }, 100);
      }
    });

    watch(
      () => route?.path,
      () => {
        showAltQuantity.value = false;
        selectedQuantity.value = 1;
      },
    );

    return {
      altQuantityInput,
      ...buttonStyle,
      money,
      quantityOptions,
      selectedQuantity,
      showAltQuantity,
      showMoreQuantityOption,
    };
  },
});
</script>

<template>
  <ButtonGroup
    :disabled="disabled"
    :fullWidth="fullWidth"
    :isLoading="isLoading"
    :size="size"
    :theme="theme"
  >
    <template #left>
      <select
        v-show="!showAltQuantity"
        v-model="selectedQuantity"
        aria-label="quantity"
        class="pl-5 pr-3 bg-transparent border-0 rounded-tl-lg rounded-bl-lg appearance-none cursor-pointer add-to-cart-select"
        :class="[
          fontColorUtilities,
          cursorUtilities,
          theme === 'white' ? 'black-chevron-down' : 'white-chevron-down',
        ]"
        data-test="quantity-options"
        :disabled="disabled || isLoading"
        style="width: 72px"
      >
        <option v-for="val in quantityOptions" class="text-black" :key="val" :value="val">
          {{ val }}
        </option>
        <option v-if="showMoreQuantityOption" class="text-black" :value="10">more...</option>
      </select>
      <input
        v-if="showAltQuantity"
        v-model.number="selectedQuantity"
        aria-label="quantity"
        class="px-3 font-semibold bg-transparent border-0 border-solid center-text add-to-cart-input"
        :class="fontColorUtilities"
        :disabled="disabled || isLoading"
        :max="maximumPiecesPerOrder"
        min="1"
        ref="altQuantityInput"
        required
        style="width: 72px"
        type="number"
        @keydown.enter.prevent="$emit('submit')"
        data-test="more-quantity"
      />
    </template>
    <template #right>
      <button
        class="flex items-center justify-center font-semibold tracking-wide bg-transparent border-0 rounded-none add-to-cart-button font-proxima-nova h-11"
        :class="[cursorUtilities, fontColorUtilities]"
        :disabled="disabled || isLoading"
        @click="$emit('submit')"
        :data-test="dataTest ? `${dataTest}-button` : undefined"
      >
        <img
          v-if="isLoading"
          class="w-3 h-3 mr-2 animate-spin"
          :class="{
            'invert-spinner': theme === 'white',
            'w-4 h-4': size !== 'small',
          }"
          src="@/assets/spinner.svg"
          alt="spinner"
        />
        <span :data-test="dataTest ? `${dataTest}-text` : undefined"><slot /></span>
      </button>
    </template>
  </ButtonGroup>
</template>

<style lang="scss" scoped>
.add-to-cart-button {
  @apply w-full inline-flex rounded-r-lg items-center justify-center text-base;
}
.add-to-cart-select {
  @apply py-3 text-base font-semibold font-proxima-nova h-11;
  line-height: normal;
  background-position: top 50% right 15px;
  width: 4rem;
}

.black-chevron-down {
  background-image: url('data:image/svg+xml,<svg width="16" height="8" xmlns="http://www.w3.org/2000/svg"><path fill="none" stroke="black" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M1 1L6.75 7L12.5 1"/></svg>');
}

.white-chevron-down {
  background-image: url('data:image/svg+xml,<svg width="16" height="8" xmlns="http://www.w3.org/2000/svg"><path fill="none" stroke="white" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M1 1L6.75 7L12.5 1"/></svg>');
}

.invert-spinner {
  filter: invert(1);
}
// Override forms.scss
input[disabled] {
  opacity: 1;
  background-color: transparent;
}
</style>

<style scoped>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
/* Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}
</style>
